/**
 * The people who eat here but do not have an account.
 *
 * Paprika has always assumed a person is a login: households are capped at two
 * members and every member is a Firebase uid. That works for deciding what to
 * cook — the two adults are the ones planning — but it cannot express who was
 * actually at the table. Children have opinions about dinner and no phone.
 *
 * An eater is therefore a name, nothing more: no auth, no permissions, no
 * invite. Their verdicts are dictated by whichever parent is holding the
 * device (see RecipeMetadata.verdicts).
 *
 * Storage is a subcollection rather than a map field on the household doc, for
 * two reasons: separate documents cannot clobber each other when both parents
 * add someone while offline, and `buildScopedPath` then routes solo users to
 * `users/{uid}/eaters` for free — a single parent with two kids is a real
 * household even when nobody has been invited.
 */

import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  getDocs,
} from 'firebase/firestore';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import { resolveScopedPath } from './householdPaths';

export interface Eater {
  /** Generated, stable, and never the name — renaming must not orphan verdicts. */
  id: string;
  name: string;
  createdAt: string; // ISO
}

/** Longest name we will store. Long enough for "Grand-maman", short enough to render. */
export const EATER_NAME_MAX = 24;

/**
 * Resolve the eaters collection for the active scope, or null when Firebase is
 * off or we have neither a uid nor a household (guest mode pre-auth).
 */
const eatersCollection = (
  uid: string | null | undefined,
  householdId: string | null | undefined
) => {
  if (!isFirebaseEnabled() || !firestore) return null;
  const path = resolveScopedPath(uid, householdId, 'eaters');
  if (!path) return null;
  const [root, id, ...rest] = path;
  return collection(firestore, root, id, ...rest);
};

/** Trim and clamp a typed name. Returns '' for anything unusable. */
export const normalizeEaterName = (raw: string): string =>
  raw.trim().replace(/\s+/g, ' ').slice(0, EATER_NAME_MAX);

/**
 * Watch the roster. Live rather than one-shot so a name added on one parent's
 * phone shows up on the other's without a reload — the same expectation the
 * household roster already sets.
 */
export const subscribeToEaters = (
  uid: string | null | undefined,
  householdId: string | null | undefined,
  onChange: (eaters: Eater[]) => void
): (() => void) => {
  const ref = eatersCollection(uid, householdId);
  if (!ref) {
    onChange([]);
    return () => {};
  }
  return onSnapshot(
    ref,
    (snap) => {
      const eaters = snap.docs.map((d) => ({
        id: d.id,
        name: (d.data().name as string) ?? '',
        createdAt: (d.data().createdAt as string) ?? '',
      }));
      // Oldest first, so the list does not reshuffle as names are edited.
      eaters.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      onChange(eaters);
    },
    () => onChange([])
  );
};

/** One-shot read, for callers outside React. */
export const fetchEaters = async (
  uid: string | null | undefined,
  householdId: string | null | undefined
): Promise<Eater[]> => {
  const ref = eatersCollection(uid, householdId);
  if (!ref) return [];
  const snap = await getDocs(ref);
  return snap.docs
    .map((d) => ({
      id: d.id,
      name: (d.data().name as string) ?? '',
      createdAt: (d.data().createdAt as string) ?? '',
    }))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
};

export const addEater = async (
  uid: string | null | undefined,
  householdId: string | null | undefined,
  rawName: string
): Promise<Eater | null> => {
  const ref = eatersCollection(uid, householdId);
  const name = normalizeEaterName(rawName);
  if (!ref || !name) return null;

  // Firestore mints the id, so two offline adds can never collide on one.
  const docRef = doc(ref);
  const eater: Eater = { id: docRef.id, name, createdAt: new Date().toISOString() };
  await setDoc(docRef, { name: eater.name, createdAt: eater.createdAt });
  return eater;
};

export const renameEater = async (
  uid: string | null | undefined,
  householdId: string | null | undefined,
  eaterId: string,
  rawName: string
): Promise<void> => {
  const ref = eatersCollection(uid, householdId);
  const name = normalizeEaterName(rawName);
  if (!ref || !name) return;
  await updateDoc(doc(ref, eaterId), { name });
};

/**
 * Remove someone from the roster.
 *
 * Deliberately leaves their past verdicts in place. They ate the food and they
 * had an opinion; deleting it would silently rewrite the household's history
 * of a recipe. Orphaned verdict keys simply stop being rendered.
 */
export const removeEater = async (
  uid: string | null | undefined,
  householdId: string | null | undefined,
  eaterId: string
): Promise<void> => {
  const ref = eatersCollection(uid, householdId);
  if (!ref) return;
  await deleteDoc(doc(ref, eaterId));
};
